import i18n from '@/i18n/i18n';
import { DOCX_MIME, formatValue, type FillVariable } from './utils';

/**
 * Merge filled form values into the tokenized .docx template and return the merged bytes.
 *
 * This is the deterministic, browser-side merge shared by the generate flow
 * (DocxTemplateGenerateFlow) and the review page (which re-merges to reconstruct the
 * editable .docx when a contract predates persisted merged-docx storage). No AI, no server.
 *
 * Leaf variables = fields the user fills by hand: real document tokens (scalars + person
 * sub-fields), minus person PARENTS (no token), mirrored fields, and signature anchors
 * (location markers, never merged). Mirrored fields are fanned out from their source.
 */
export async function mergeDocx({
  tokenizedUrl,
  variables,
  values,
}: {
  tokenizedUrl: string;
  variables: FillVariable[];
  values: Record<string, string>;
}): Promise<Uint8Array> {
  const leafVariables = variables.filter(
    (v) => v.type !== 'person' && !v.valueCopyFrom && v.subKind !== 'signature'
  );

  const locale = i18n.language || 'en';
  const data: Record<string, string> = {};
  for (const v of leafVariables) {
    data[v.name] = formatValue(v.type, values[v.name], locale);
  }
  // Fan mirrored fields out from their source (e.g. a company name repeated across the doc).
  for (const v of variables) {
    if (v.valueCopyFrom) data[v.name] = data[v.valueCopyFrom] ?? '';
  }

  const fileRes = await fetch(tokenizedUrl);
  if (!fileRes.ok) {
    throw new Error('Could not load the template document.');
  }
  const templateBytes = await fileRes.arrayBuffer();

  // Load the merge engine on demand — easy-template-x (+ deps) is heavy and only needed here.
  // `process` returns the same binary type it was given (here an ArrayBuffer).
  const { TemplateHandler } = await import('easy-template-x');
  const merged = await new TemplateHandler().process(templateBytes, data);
  return merged instanceof Uint8Array ? merged : new Uint8Array(merged as ArrayBuffer);
}

export { DOCX_MIME };
